import React, { useState, useEffect } from 'react';
import {
  Shield,
  Smartphone,
  Key,
  Copy,
  Download,
  Check,
  X,
  Loader2,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { twoFactorAPI } from '@services/api';
import Button from '@components/common/Button';
import Input from '@components/common/Input';
import Alert from '@components/common/Alert';

interface TwoFactorSetupProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type SetupStep = 'intro' | 'qrcode' | 'verify' | 'backup' | 'complete';

const TwoFactorSetup: React.FC<TwoFactorSetupProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [step, setStep] = useState<SetupStep>('intro');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Setup data from API
  const [secret, setSecret] = useState<string>('');
  const [qrCodeUri, setQrCodeUri] = useState<string>('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);

  // Verification
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  // Copy state
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep('intro');
      setSecret('');
      setQrCodeUri('');
      setBackupCodes([]);
      setVerificationCode('');
      setError(null);
    }
  }, [isOpen]);

  const handleStartSetup = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await twoFactorAPI.setup();
      setSecret(response.secret);
      setQrCodeUri(response.qr_code_uri);
      setBackupCodes(response.backup_codes);
      setStep('qrcode');
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to initiate 2FA setup';
      setError(errorMessage);
      toast.error('Failed to start 2FA setup');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async () => {
    if (verificationCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      await twoFactorAPI.verify(verificationCode);
      setStep('backup');
      toast.success('2FA verified successfully!');
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Invalid verification code';
      setError(errorMessage);
      toast.error('Verification failed');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCopySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopiedSecret(true);
      toast.success('Secret copied to clipboard');
      setTimeout(() => setCopiedSecret(false), 2000);
    } catch {
      toast.error('Failed to copy secret');
    }
  };

  const handleCopyBackupCodes = async () => {
    try {
      const codesText = backupCodes.join('\n');
      await navigator.clipboard.writeText(codesText);
      setCopiedCodes(true);
      toast.success('Backup codes copied to clipboard');
      setTimeout(() => setCopiedCodes(false), 2000);
    } catch {
      toast.error('Failed to copy backup codes');
    }
  };

  const handleDownloadBackupCodes = () => {
    const codesText = `PixelShare 2FA Backup Codes
Generated: ${new Date().toISOString()}

IMPORTANT: Keep these codes safe! Each code can only be used once.

${backupCodes.map((code, i) => `${i + 1}. ${code}`).join('\n')}

If you lose access to your authenticator app, you can use one of these
codes to sign in. After using a code, it will no longer be valid.
`;

    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'pixelshare-2fa-backup-codes.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Backup codes downloaded');
  };

  const handleComplete = () => {
    onSuccess();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center">
                <Shield size={20} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Set Up Two-Factor Authentication
                </h3>
                <p className="text-sm text-gray-500">
                  Add an extra layer of security to your account
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X size={20} />
            </button>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center justify-center gap-2 mb-6">
            {['intro', 'qrcode', 'verify', 'backup'].map((s, i) => (
              <React.Fragment key={s}>
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step === s
                      ? 'bg-primary-600 text-white'
                      : ['intro', 'qrcode', 'verify', 'backup'].indexOf(step) > i
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {['intro', 'qrcode', 'verify', 'backup'].indexOf(step) > i ? (
                    <Check size={16} />
                  ) : (
                    i + 1
                  )}
                </div>
                {i < 3 && (
                  <div
                    className={`w-8 h-1 ${
                      ['intro', 'qrcode', 'verify', 'backup'].indexOf(step) > i
                        ? 'bg-green-500'
                        : 'bg-gray-200'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Content */}
          <div className="space-y-4">
            {/* Step 1: Introduction */}
            {step === 'intro' && (
              <>
                <div className="text-center space-y-4">
                  <div className="flex justify-center">
                    <Smartphone size={64} className="text-primary-500" />
                  </div>
                  <p className="text-gray-600">
                    Two-factor authentication adds an extra layer of security by
                    requiring a code from your phone in addition to your password.
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
                    <h4 className="font-medium text-blue-800 mb-2">
                      You'll need:
                    </h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>
                        An authenticator app (Google Authenticator, Authy, 1Password,
                        etc.)
                      </li>
                      <li>
                        Access to your phone to scan a QR code
                      </li>
                    </ul>
                  </div>
                </div>

                {error && (
                  <Alert
                    type="error"
                    message={error}
                    dismissible
                    onDismiss={() => setError(null)}
                  />
                )}

                <div className="flex justify-end gap-3">
                  <Button variant="ghost" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleStartSetup}
                    loading={isLoading}
                    icon={<Shield size={16} />}
                  >
                    Get Started
                  </Button>
                </div>
              </>
            )}

            {/* Step 2: QR Code */}
            {step === 'qrcode' && (
              <>
                <div className="text-center space-y-4">
                  <p className="text-gray-600">
                    Scan this QR code with your authenticator app:
                  </p>

                  {/* QR Code Display */}
                  <div className="flex justify-center">
                    <div className="bg-white p-4 border-2 border-gray-200 rounded-lg">
                      {qrCodeUri ? (
                        <img
                          src={qrCodeUri}
                          alt="2FA QR Code"
                          className="w-48 h-48"
                        />
                      ) : (
                        <div className="w-48 h-48 flex items-center justify-center">
                          <Loader2 className="animate-spin text-gray-400" size={32} />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Manual Entry */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-sm text-gray-500 mb-2">
                      Can't scan? Enter this code manually:
                    </p>
                    <div className="flex items-center justify-center gap-2">
                      <code className="bg-white px-3 py-2 rounded border text-sm font-mono">
                        {secret}
                      </code>
                      <button
                        onClick={handleCopySecret}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                        title="Copy secret"
                      >
                        {copiedSecret ? (
                          <Check size={16} className="text-green-500" />
                        ) : (
                          <Copy size={16} />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between gap-3">
                  <Button variant="ghost" onClick={() => setStep('intro')}>
                    Back
                  </Button>
                  <Button variant="primary" onClick={() => setStep('verify')}>
                    I've scanned the code
                  </Button>
                </div>
              </>
            )}

            {/* Step 3: Verify */}
            {step === 'verify' && (
              <>
                <div className="text-center space-y-4">
                  <p className="text-gray-600">
                    Enter the 6-digit code from your authenticator app to verify:
                  </p>

                  <div className="max-w-xs mx-auto">
                    <Input
                      type="text"
                      value={verificationCode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setVerificationCode(value);
                      }}
                      placeholder="000000"
                      className="text-center text-2xl tracking-widest font-mono"
                      maxLength={6}
                      autoFocus
                      leftIcon={<Key size={16} />}
                    />
                  </div>

                  <p className="text-sm text-gray-500">
                    The code changes every 30 seconds
                  </p>
                </div>

                {error && (
                  <Alert
                    type="error"
                    message={error}
                    dismissible
                    onDismiss={() => setError(null)}
                  />
                )}

                <div className="flex justify-between gap-3">
                  <Button variant="ghost" onClick={() => setStep('qrcode')}>
                    Back
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleVerify}
                    loading={isVerifying}
                    disabled={verificationCode.length !== 6}
                    icon={<Check size={16} />}
                  >
                    Verify & Enable
                  </Button>
                </div>
              </>
            )}

            {/* Step 4: Backup Codes */}
            {step === 'backup' && (
              <>
                <div className="space-y-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="text-yellow-600 mt-0.5" size={20} />
                      <div>
                        <h4 className="font-medium text-yellow-800">
                          Save Your Backup Codes
                        </h4>
                        <p className="text-sm text-yellow-700 mt-1">
                          If you lose access to your authenticator app, you can use
                          these codes to sign in. Each code can only be used once.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Backup Codes Grid */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-2">
                      {backupCodes.map((code, index) => (
                        <div
                          key={index}
                          className="bg-white px-3 py-2 rounded border text-sm font-mono text-center"
                        >
                          {code}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-center gap-3">
                    <Button
                      variant="outline"
                      onClick={handleCopyBackupCodes}
                      icon={
                        copiedCodes ? (
                          <Check size={16} className="text-green-500" />
                        ) : (
                          <Copy size={16} />
                        )
                      }
                    >
                      {copiedCodes ? 'Copied!' : 'Copy Codes'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleDownloadBackupCodes}
                      icon={<Download size={16} />}
                    >
                      Download
                    </Button>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-4">
                  <Button variant="primary" onClick={handleComplete}>
                    I've saved my codes
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Disable 2FA Modal Component
interface TwoFactorDisableProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const TwoFactorDisable: React.FC<TwoFactorDisableProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setPassword('');
      setCode('');
      setError(null);
    }
  }, [isOpen]);

  const handleDisable = async () => {
    if (!code || code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await twoFactorAPI.disable(password, code);
      toast.success('Two-factor authentication disabled');
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to disable 2FA';
      setError(errorMessage);
      toast.error('Failed to disable 2FA');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-red-100 text-red-600 rounded-full flex items-center justify-center">
              <AlertTriangle size={20} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Disable Two-Factor Authentication
              </h3>
              <p className="text-sm text-gray-500">
                This will make your account less secure
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <Alert
              type="warning"
              message="Disabling 2FA will remove an important security layer from your account. Only proceed if you're sure."
            />

            <Input
              type="password"
              label="Password (if applicable)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              leftIcon={<Key size={16} />}
              showPasswordToggle
            />

            <Input
              type="text"
              label="Authentication Code"
              value={code}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setCode(value);
              }}
              placeholder="000000"
              maxLength={6}
              leftIcon={<Smartphone size={16} />}
              helperText="Enter the 6-digit code from your authenticator app"
            />

            {error && (
              <Alert
                type="error"
                message={error}
                dismissible
                onDismiss={() => setError(null)}
              />
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button variant="ghost" onClick={onClose} icon={<X size={16} />}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDisable}
              loading={isLoading}
              disabled={code.length !== 6}
              icon={<Shield size={16} />}
            >
              Disable 2FA
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Backup Codes Regenerate Modal
interface BackupCodesRegenerateProps {
  isOpen: boolean;
  onClose: () => void;
}

export const BackupCodesRegenerate: React.FC<BackupCodesRegenerateProps> = ({
  isOpen,
  onClose,
}) => {
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newCodes, setNewCodes] = useState<string[]>([]);
  const [copiedCodes, setCopiedCodes] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setCode('');
      setNewCodes([]);
      setError(null);
    }
  }, [isOpen]);

  const handleRegenerate = async () => {
    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const codes = await twoFactorAPI.regenerateBackupCodes(code);
      setNewCodes(codes);
      toast.success('Backup codes regenerated');
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to regenerate codes';
      setError(errorMessage);
      toast.error('Failed to regenerate backup codes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyBackupCodes = async () => {
    try {
      const codesText = newCodes.join('\n');
      await navigator.clipboard.writeText(codesText);
      setCopiedCodes(true);
      toast.success('Backup codes copied to clipboard');
      setTimeout(() => setCopiedCodes(false), 2000);
    } catch {
      toast.error('Failed to copy backup codes');
    }
  };

  const handleDownloadBackupCodes = () => {
    const codesText = `PixelShare 2FA Backup Codes
Generated: ${new Date().toISOString()}

IMPORTANT: Keep these codes safe! Each code can only be used once.
Your previous backup codes are no longer valid.

${newCodes.map((c, i) => `${i + 1}. ${c}`).join('\n')}
`;

    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'pixelshare-2fa-backup-codes.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Backup codes downloaded');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center">
              <RefreshCw size={20} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Regenerate Backup Codes
              </h3>
              <p className="text-sm text-gray-500">
                Get new backup codes for your account
              </p>
            </div>
          </div>

          {newCodes.length === 0 ? (
            <div className="space-y-4">
              <Alert
                type="warning"
                message="This will invalidate all your existing backup codes. Make sure to save the new ones."
              />

              <Input
                type="text"
                label="Authentication Code"
                value={code}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setCode(value);
                }}
                placeholder="000000"
                maxLength={6}
                autoFocus
                leftIcon={<Smartphone size={16} />}
                helperText="Enter the 6-digit code from your authenticator app"
              />

              {error && (
                <Alert
                  type="error"
                  message={error}
                  dismissible
                  onDismiss={() => setError(null)}
                />
              )}

              <div className="flex justify-end gap-3">
                <Button variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleRegenerate}
                  loading={isLoading}
                  disabled={code.length !== 6}
                  icon={<RefreshCw size={16} />}
                >
                  Regenerate
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <Alert
                type="success"
                message="Your new backup codes are ready. Save them securely."
              />

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-2">
                  {newCodes.map((c, index) => (
                    <div
                      key={index}
                      className="bg-white px-3 py-2 rounded border text-sm font-mono text-center"
                    >
                      {c}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-center gap-3">
                <Button
                  variant="outline"
                  onClick={handleCopyBackupCodes}
                  icon={
                    copiedCodes ? (
                      <Check size={16} className="text-green-500" />
                    ) : (
                      <Copy size={16} />
                    )
                  }
                >
                  {copiedCodes ? 'Copied!' : 'Copy Codes'}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDownloadBackupCodes}
                  icon={<Download size={16} />}
                >
                  Download
                </Button>
              </div>

              <div className="flex justify-end">
                <Button variant="primary" onClick={onClose}>
                  Done
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TwoFactorSetup;
